import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { money } from "@/lib/api";
import DateRange from "@/components/DateRange";
import SmartPicks from "@/components/SmartPicks";
import { useAuth } from "@/context/AuthContext";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Percent,
  Target,
  Trophy,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
} from "lucide-react";

const GREEN = "#34C759";
const RED = "#FF3B30";
const BLUE = "#007AFF";

function Kpi({ label, value, sub, icon: Icon, tone = "default", delay = 0, testid }) {
  const toneColor =
    tone === "up" ? "text-[#34C759]" : tone === "down" ? "text-[#FF3B30]" : "text-foreground";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="glass rounded-3xl p-5 sm:p-6"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-muted-foreground">
          {label}
        </span>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className={`font-display text-3xl sm:text-4xl font-extrabold ${toneColor}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-1.5">{sub}</div>}
    </motion.div>
  );
}

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass rounded-xl px-3 py-2 text-xs">
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color }}>
          {p.name}: {money(p.value)}
        </div>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [range, setRange] = useState({ start: "", end: "", preset: "all" });
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [cats, setCats] = useState([]);
  const [season, setSeason] = useState("all");
  const [seasons, setSeasons] = useState([]);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/bets/meta/seasons").then((r) => setSeasons(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setInsightsLoading(true);
    setInsights(null);
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    if (category !== "all") params.category = category;
    if (season !== "all") params.season = season;
    api
      .get("/analytics/insights", { params })
      .then((r) => setInsights(r.data))
      .catch(() => setInsights(null))
      .finally(() => setInsightsLoading(false));
  }, [range, category, season]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    if (category !== "all") params.category = category;
    if (season !== "all") params.season = season;
    try {
      const [s, t] = await Promise.all([
        api.get("/analytics/summary", { params }),
        api.get("/analytics/trends", { params }),
      ]);
      setSummary(s.data);
      setTrends(t.data);
    } finally {
      setLoading(false);
    }
  }, [range, category, season]);

  useEffect(() => {
    load();
  }, [load]);

  const profitTone = summary && summary.profit > 0 ? "up" : summary && summary.profit < 0 ? "down" : "default";
  const markets = trends?.by_market || [];
  const categories = trends?.by_category || [];
  const seasonsBreak = trends?.by_season || [];
  const best = markets.filter((m) => m.profit > 0)[0];
  const worst = [...markets].reverse().filter((m) => m.profit < 0)[0];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            Hey {user?.name?.split(" ")[0] || "there"} 👋
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Your betting performance at a glance.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <DateRange range={range} setRange={setRange} />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="rounded-full h-9 w-48" data-testid="dashboard-category-filter">
              <SelectValue placeholder="All competitions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All competitions</SelectItem>
              {cats.map((c) => (
                <SelectItem key={c.id} value={c.name}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {seasons.length > 0 && (
            <Select value={season} onValueChange={setSeason}>
              <SelectTrigger className="rounded-full h-9 w-36" data-testid="dashboard-season-filter">
                <SelectValue placeholder="All seasons" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All seasons</SelectItem>
                {seasons.map((sn) => (
                  <SelectItem key={sn} value={sn}>
                    {sn}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {loading || !summary ? (
        <div className="h-64 flex items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : summary.total_bets === 0 ? (
        <div className="glass rounded-3xl p-12 text-center" data-testid="dashboard-empty">
          <Trophy className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-display text-xl font-bold">No bets yet</h3>
          <p className="text-muted-foreground text-sm mt-1">
            Head to the Bets tab to upload a screenshot or add one manually.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Kpi
              label="Profit / Loss"
              value={money(summary.profit)}
              sub={`${summary.settled_bets} settled bets`}
              icon={summary.profit >= 0 ? TrendingUp : TrendingDown}
              tone={profitTone}
              delay={0}
              testid="kpi-profit"
            />
            <Kpi
              label="ROI"
              value={`${summary.roi}%`}
              sub={`${money(summary.total_staked)} staked`}
              icon={Percent}
              tone={summary.roi >= 0 ? "up" : "down"}
              delay={0.05}
              testid="kpi-roi"
            />
            <Kpi
              label="Win Rate"
              value={`${summary.win_rate}%`}
              sub={`${summary.wins}W · ${summary.losses}L`}
              icon={Target}
              delay={0.1}
              testid="kpi-winrate"
            />
            <Kpi
              label="Total Bets"
              value={summary.total_bets}
              sub={`${summary.pending} pending`}
              icon={Trophy}
              delay={0.15}
              testid="kpi-total"
            />
          </div>

          <SmartPicks insights={insights} insightsLoading={insightsLoading} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="glass rounded-3xl p-5 sm:p-6 lg:col-span-2" data-testid="profit-chart">
              <h3 className="font-display font-bold text-lg mb-4">Cumulative Profit</h3>
              {summary.profit_timeline.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={summary.profit_timeline}>
                    <defs>
                      <linearGradient id="pg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={BLUE} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} width={40} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="cumulative" name="Cumulative" stroke={BLUE} strokeWidth={2.5} fill="url(#pg)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-sm py-12 text-center">Not enough data.</p>
              )}
            </div>

            <div className="space-y-4">
              <div className="glass rounded-3xl p-5" data-testid="best-market">
                <div className="flex items-center gap-2 text-[#34C759] mb-2">
                  <ArrowUpRight className="h-4 w-4" />
                  <span className="text-sm font-semibold">Best Market</span>
                </div>
                {best ? (
                  <>
                    <div className="font-display font-bold text-lg">{best.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {money(best.profit)} · {best.win_rate}% win
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-muted-foreground">No profitable market yet.</div>
                )}
              </div>
              <div className="glass rounded-3xl p-5" data-testid="worst-market">
                <div className="flex items-center gap-2 text-[#FF3B30] mb-2">
                  <ArrowDownRight className="h-4 w-4" />
                  <span className="text-sm font-semibold">Leaking Money</span>
                </div>
                {worst ? (
                  <>
                    <div className="font-display font-bold text-lg">{worst.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {money(worst.profit)} · {worst.win_rate}% win
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-muted-foreground">No losing market. Nice!</div>
                )}
              </div>
            </div>
          </div>

          <div className="glass rounded-3xl p-5 sm:p-6" data-testid="market-breakdown">
            <h3 className="font-display font-bold text-lg mb-4">Profit by Market</h3>
            {markets.length ? (
              <ResponsiveContainer width="100%" height={Math.max(220, markets.length * 46)}>
                <BarChart data={markets} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "hsl(var(--foreground))" }} tickLine={false} axisLine={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
                  <Bar dataKey="profit" name="Profit" radius={[0, 8, 8, 0]}>
                    {markets.map((m, i) => (
                      <Cell key={i} fill={m.profit >= 0 ? GREEN : RED} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted-foreground text-sm py-8 text-center">
                No settled bets in this range.
              </p>
            )}
          </div>

          <div className="glass rounded-3xl p-5 sm:p-6" data-testid="category-breakdown">
            <h3 className="font-display font-bold text-lg mb-1">Profit by Competition</h3>
            <p className="text-muted-foreground text-xs mb-4">
              How each competition / category is performing
            </p>
            {categories.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {categories.map((c) => (
                  <div
                    key={c.name}
                    className="flex items-center justify-between rounded-2xl bg-secondary/50 px-4 py-3"
                    data-testid="category-stat"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.bets} bets · {c.win_rate}% win · {c.roi}% ROI
                      </div>
                    </div>
                    <div
                      className={`font-display font-bold text-lg shrink-0 ${
                        c.profit > 0
                          ? "text-[#34C759]"
                          : c.profit < 0
                          ? "text-[#FF3B30]"
                          : "text-muted-foreground"
                      }`}
                    >
                      {c.profit > 0 ? "+" : ""}
                      {money(c.profit)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm py-8 text-center">
                Tag your bets with a tournament to see this breakdown.
              </p>
            )}
          </div>

          <div className="glass rounded-3xl p-5 sm:p-6" data-testid="season-breakdown">
            <h3 className="font-display font-bold text-lg mb-1">Profit by Season</h3>
            <p className="text-muted-foreground text-xs mb-4">
              Compare your performance across seasons
            </p>
            {seasonsBreak.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {seasonsBreak.map((c) => (
                  <div
                    key={c.name}
                    className="flex items-center justify-between rounded-2xl bg-secondary/50 px-4 py-3"
                    data-testid="season-stat"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.bets} bets · {c.win_rate}% win · {c.roi}% ROI
                      </div>
                    </div>
                    <div
                      className={`font-display font-bold text-lg shrink-0 ${
                        c.profit > 0
                          ? "text-[#34C759]"
                          : c.profit < 0
                          ? "text-[#FF3B30]"
                          : "text-muted-foreground"
                      }`}
                    >
                      {c.profit > 0 ? "+" : ""}
                      {money(c.profit)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm py-8 text-center">
                Season is auto-detected when you upload a slip, or add it manually.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
